import { useCallback, useEffect, useRef, useState } from "react";
import {
  draftToNumber,
  formatRisk,
  sanitizeAmount,
} from "../utils/format";
import {
  convertRiskValue,
  getRiskValidationError,
  normalizeRiskField,
  toWagerPercent,
} from "../utils/risk";
import { useSavedRiskSettings } from "./useSavedRiskSettings";

/**
 * Risk mode, TP/SL drafts, Save toggle, saved settings, and wager-percent sync.
 *
 * @param {object} params
 * @param {number} params.wager
 * @param {(value: number|null) => void} params.setTakeProfitPercent
 * @param {(value: number|null) => void} params.setStopLossPercent
 */
export const useRiskSettings = ({
  wager,
  setTakeProfitPercent,
  setStopLossPercent,
}) => {
  const [mode, setMode] = useState("$");
  const [saveRisk, setSaveRisk] = useState(false);
  const [tpValue, setTpValue] = useState(0);
  const [slValue, setSlValue] = useState(0);
  const [tpDraft, setTpDraft] = useState(formatRisk(0));
  const [slDraft, setSlDraft] = useState(formatRisk(0));
  const intentRef = useRef({ tp: 0, sl: 0 });
  const [intentVersion, setIntentVersion] = useState(0);
  // Saved settings arrive late: they must not overwrite what the user has entered
  const isEditedRef = useRef(false);
  // Removing is only for settings that exist: typing with Save off stays local
  const hasSavedSettingsRef = useRef(false);

  const applySavedSettings = useCallback((settings) => {
    if (isEditedRef.current) return;

    hasSavedSettingsRef.current = true;
    intentRef.current = { tp: settings.takeProfit, sl: settings.stopLoss };
    setMode(settings.mode);
    setSaveRisk(true);
    setTpValue(settings.takeProfit);
    setSlValue(settings.stopLoss);
    setTpDraft(formatRisk(settings.takeProfit));
    setSlDraft(formatRisk(settings.stopLoss));
  }, []);

  const saveSettings = useSavedRiskSettings(applySavedSettings);

  useEffect(() => {
    if (!isEditedRef.current) return;

    const settings = {
      mode,
      takeProfit: intentRef.current.tp,
      stopLoss: intentRef.current.sl,
    };

    if (saveRisk) {
      hasSavedSettingsRef.current = true;
      saveSettings(settings);
    } else if (hasSavedSettingsRef.current) {
      hasSavedSettingsRef.current = false;
      saveSettings(null);
    }
  }, [saveRisk, mode, intentVersion, saveSettings]);

  useEffect(() => {
    setTakeProfitPercent(toWagerPercent(tpValue, mode, wager));
    setStopLossPercent(toWagerPercent(slValue, mode, wager));
  }, [tpValue, slValue, mode, wager, setTakeProfitPercent, setStopLossPercent]);

  const setTpIntent = (value) => {
    // Leaving a field without a change isn't an edit
    if (value === intentRef.current.tp) return;

    isEditedRef.current = true;
    intentRef.current.tp = value;
    setTpValue(value);
    setIntentVersion((version) => version + 1);
  };

  const setSlIntent = (value) => {
    if (value === intentRef.current.sl) return;

    isEditedRef.current = true;
    intentRef.current.sl = value;
    setSlValue(value);
    setIntentVersion((version) => version + 1);
  };

  const setRiskDraft = (setterDraft, setterValue) => (raw) => {
    const next = sanitizeAmount(raw);

    setterDraft(next);
    setterValue(draftToNumber(next));
  };

  const onRiskInput = (setterDraft, setterValue) => (event) => {
    setRiskDraft(setterDraft, setterValue)(event.target.value);
  };

  const onRiskBlur = (draft, setterDraft, setterValue) => () => {
    const { value, draft: nextDraft } = normalizeRiskField(draftToNumber(draft));

    setterValue(value);
    setterDraft(nextDraft);
  };

  const switchMode = (nextMode) => {
    if (nextMode === mode) return;

    isEditedRef.current = true;
    const nextTp = convertRiskValue(tpValue, nextMode, wager);
    const nextSl = convertRiskValue(slValue, nextMode, wager);

    setTpIntent(nextTp);
    setSlIntent(nextSl);
    setTpDraft(formatRisk(nextTp));
    setSlDraft(formatRisk(nextSl));
    setMode(nextMode);
  };

  const toggleSaveRisk = () => {
    isEditedRef.current = true;
    setSaveRisk((current) => !current);
  };

  return {
    mode,
    saveRisk,
    tpDraft,
    slDraft,
    switchMode,
    toggleSaveRisk,
    onTpChange: onRiskInput(setTpDraft, setTpIntent),
    onSlChange: onRiskInput(setSlDraft, setSlIntent),
    onTpBlur: onRiskBlur(tpDraft, setTpDraft, setTpIntent),
    onSlBlur: onRiskBlur(slDraft, setSlDraft, setSlIntent),
    setTpInput: setRiskDraft(setTpDraft, setTpIntent),
    setSlInput: setRiskDraft(setSlDraft, setSlIntent),
    getValidationError: () =>
      getRiskValidationError({ mode, tpValue, slValue, wager }),
  };
};
